import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import PriceField from "@dashboard/components/PriceField";
import { OrderDetailsFragment, useOrderShippingPriceOverrideMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { getDefaultNotifierSuccessErrorData } from "@dashboard/hooks/useNotifier/utils";
import { buttonMessages } from "@dashboard/intl";
import { Box, Button, EditIcon, Popover, sprinkles, Text } from "@saleor/macaw-ui-next";
import { ChangeEvent, useState } from "react";
import { useIntl } from "react-intl";

import { messages } from "./messages";

// Mirrors the backend's private-metadata key (saleor/graphql/order/mutations/utils.py).
const SHIPPING_PRICE_OVERRIDE_KEY = "shipping_price_override";

export const isShippingPriceOverridden = (
  order: Pick<OrderDetailsFragment, "privateMetadata">,
): boolean =>
  (order.privateMetadata ?? []).some(
    entry => entry.key === SHIPPING_PRICE_OVERRIDE_KEY && entry.value !== "",
  );

interface OrderShippingPriceEditProps {
  order: OrderDetailsFragment;
}

// One-off shipping price for a draft/unconfirmed order: replaces the shipping
// method's price for this order only, without touching the method itself.
export const OrderShippingPriceEdit = ({ order }: OrderShippingPriceEditProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const overridden = isShippingPriceOverridden(order);
  const [overrideShippingPrice, overrideOpts] = useOrderShippingPriceOverrideMutation({
    onCompleted: ({ orderShippingPriceOverride }) => {
      const errors = orderShippingPriceOverride?.errors ?? [];

      notify(getDefaultNotifierSuccessErrorData(errors, intl));

      if (!errors.length) {
        setOpen(false);
      }
    },
  });
  const priceValue = parseFloat(price);
  const invalid = isNaN(priceValue) || priceValue < 0;
  const handleOpenChange = (value: boolean) => {
    if (value) {
      setPrice(String(order.shippingPrice.gross.amount));
    }

    setOpen(value);
  };
  const submit = (value: number | null) =>
    overrideShippingPrice({ variables: { id: order.id, price: value } });

  return (
    <Box display="inline-flex" alignItems="center" gap={1}>
      {overridden && (
        <Text size={2} color="default2">
          ({intl.formatMessage(messages.manual)})
        </Text>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger>
          <Button
            variant="tertiary"
            size="small"
            icon={<EditIcon size="small" />}
            title={intl.formatMessage(messages.edit)}
            data-test-id="edit-shipping-price"
          />
        </Popover.Trigger>
        <Popover.Content align="end" className={sprinkles({ zIndex: "3" })}>
          <Box
            boxShadow="defaultOverlay"
            backgroundColor="default1"
            borderRadius={3}
            padding={4}
            display="grid"
            gap={3}
            __width={300}
          >
            <Text fontWeight="bold">{intl.formatMessage(messages.title)}</Text>
            <PriceField
              label={intl.formatMessage(messages.priceLabel)}
              value={price}
              currencySymbol={order.shippingPrice.gross.currency}
              error={invalid}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPrice(event.target.value)}
              data-test-id="shipping-price-input"
            />
            <Text size={2} color="default2">
              {intl.formatMessage(messages.hint)}
            </Text>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              {overridden && (
                <Button
                  variant="secondary"
                  onClick={() => submit(null)}
                  disabled={overrideOpts.loading}
                  data-test-id="reset-shipping-price"
                >
                  {intl.formatMessage(messages.reset)}
                </Button>
              )}
              <ConfirmButton
                variant="primary"
                disabled={invalid}
                transitionState={overrideOpts.status}
                onClick={() => submit(Math.round(priceValue * 100) / 100)}
                data-test-id="save-shipping-price"
              >
                {intl.formatMessage(buttonMessages.save)}
              </ConfirmButton>
            </Box>
          </Box>
        </Popover.Content>
      </Popover>
    </Box>
  );
};
